<template>
  <Teleport to="body">
    <transition name="app-alert-fade">
      <div v-if="visible" class="app-alert" :class="{ 'app-alert--site': variant === 'site' }" role="presentation" @click.self="close">
        <section
          class="app-alert__panel"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="app-alert-title"
          aria-describedby="app-alert-message"
        >
          <div class="app-alert__mark">!</div>
          <div class="app-alert__content">
            <p id="app-alert-title" class="app-alert__title">{{ title }}</p>
            <p id="app-alert-message" class="app-alert__message">{{ message }}</p>
          </div>
          <button ref="closeButton" class="app-alert__button" type="button" @click="close">
            OK
          </button>
        </section>
      </div>
    </transition>
  </Teleport>
</template>

<script>
export default {
  name: 'AppAlertDialog',
  props: {
    // 'site' follows the public design (src/styles/site.css); /admin keeps the original look.
    variant: {
      type: String,
      default: 'legacy',
    },
  },
  data() {
    return {
      visible: false,
      message: '',
      title: 'Notice',
      queue: [],
    };
  },
  mounted() {
    window.addEventListener('app-alert', this.handleAlert);
    window.addEventListener('keydown', this.handleKeydown);
  },
  beforeUnmount() {
    window.removeEventListener('app-alert', this.handleAlert);
    window.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    handleAlert(event) {
      const detail = event.detail || {};
      const nextAlert = {
        title: detail.title || 'Notice',
        message: String(detail.message ?? ''),
      };

      if (this.visible) {
        this.queue.push(nextAlert);
        return;
      }

      this.show(nextAlert);
    },
    show(alert) {
      this.title = alert.title;
      this.message = alert.message;
      this.visible = true;
      this.$nextTick(() => {
        this.$refs.closeButton?.focus();
      });
    },
    close() {
      this.visible = false;

      if (!this.queue.length) return;

      const nextAlert = this.queue.shift();
      window.setTimeout(() => this.show(nextAlert), 180);
    },
    handleKeydown(event) {
      if (!this.visible) return;

      if (event.key === 'Escape' || event.key === 'Enter') {
        this.close();
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use '../utils/_colors.scss' as *;

.app-alert {
  align-items: center;
  background:
    radial-gradient(circle at 18% 10%, rgba(54, 79, 161, 0.26), transparent 34rem),
    rgba(31, 44, 92, 0.58);
  backdrop-filter: blur(10px);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 24px;
  position: fixed;
  z-index: 9999;
}

.app-alert__panel {
  align-items: center;
  background: linear-gradient(145deg, #ffffff 0%, #f3f7ff 100%);
  border: 1px solid rgba(54, 79, 161, 0.18);
  border-radius: 28px;
  box-shadow: 0 28px 80px rgba(31, 44, 92, 0.28);
  color: $blue-1;
  display: grid;
  gap: 18px;
  grid-template-columns: auto 1fr;
  max-width: 440px;
  padding: 28px;
  text-align: left;
  width: min(100%, 440px);
}

.app-alert__mark {
  align-items: center;
  background: linear-gradient(135deg, $blue-2, $blue-1);
  border-radius: 20px;
  box-shadow: 0 14px 26px rgba(31, 44, 92, 0.28);
  color: #ffffff;
  display: flex;
  font-size: 26px;
  font-weight: 800;
  height: 56px;
  justify-content: center;
  width: 56px;
}

.app-alert__content {
  min-width: 0;
}

.app-alert__title {
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 6px;
}

.app-alert__message {
  color: #4f5e78;
  font-size: 15px;
  line-height: 1.55;
  margin: 0;
  overflow-wrap: anywhere;
  white-space: pre-line;
}

.app-alert__button {
  background: $blue-1;
  border: 0;
  border-radius: 999px;
  box-shadow: 0 12px 24px rgba(31, 44, 92, 0.22);
  color: #ffffff;
  cursor: pointer;
  font: inherit;
  font-weight: 700;
  grid-column: 1 / -1;
  justify-self: end;
  min-width: 112px;
  padding: 12px 24px;
  transition: transform 160ms ease, box-shadow 160ms ease, background 160ms ease;
}

.app-alert__button:hover,
.app-alert__button:focus-visible {
  background: $blue-2;
  box-shadow: 0 16px 28px rgba(31, 44, 92, 0.28);
  outline: none;
  transform: translateY(-1px);
}

.app-alert-fade-enter-active,
.app-alert-fade-leave-active {
  transition: opacity 180ms ease;
}

.app-alert-fade-enter-active .app-alert__panel,
.app-alert-fade-leave-active .app-alert__panel {
  transition: transform 180ms ease, opacity 180ms ease;
}

.app-alert-fade-enter-from,
.app-alert-fade-leave-to {
  opacity: 0;
}

.app-alert-fade-enter-from .app-alert__panel,
.app-alert-fade-leave-to .app-alert__panel {
  opacity: 0;
  transform: translateY(14px) scale(0.98);
}

/* Public site variant: design tokens from src/styles/site.css (:root). */
.app-alert--site {
  background: rgba(22, 26, 63, 0.55);
  backdrop-filter: blur(6px);

  .app-alert__panel {
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: 22px;
    box-shadow: var(--shadow);
    color: var(--ink);
    font-family: var(--text);
  }

  .app-alert__mark {
    background: var(--gold);
    border-radius: 12px;
    box-shadow: none;
    color: var(--navy-deep);
    font-family: var(--display);
    font-size: 22px;
    font-weight: 700;
    height: 46px;
    width: 46px;
  }

  .app-alert__title {
    font-family: var(--display);
    font-size: 20px;
    font-weight: 600;
    letter-spacing: -0.01em;
  }

  .app-alert__message {
    color: var(--ink-2);
    font-size: 16px;
  }

  .app-alert__button {
    background: var(--primary);
    box-shadow: none;
    color: var(--on-primary);
    font-family: var(--display);
    font-size: 16px;
    font-weight: 600;
    padding: 12px 22px;
  }

  .app-alert__button:hover {
    background: var(--primary);
    box-shadow: none;
  }

  .app-alert__button:focus-visible {
    outline: 3px solid var(--focus);
    outline-offset: 3px;
  }
}

@media (max-width: 520px) {
  .app-alert {
    align-items: flex-end;
    padding: 16px;
  }

  .app-alert__panel {
    border-radius: 24px;
    padding: 22px;
  }
}
</style>
